/**
 * Per-user Gmail authorisation for the TA Action Board.
 *
 * Three ways to obtain an access token, tried in this order:
 *
 * 1. SERVER GRANT (the MyNoats pattern from FINN Spaces — gmailServerGrant.ts). After one consent
 *    through Google's own page, the proxy holds a refresh token for the recruiter and mints one-hour
 *    access tokens from it on request (`POST /api/ta-action-board/gmail/token`) — no window, no
 *    click, works on a fresh session and after a reload, and a timer below renews the token before
 *    it lapses so a long visit never sees the reconnect banner. This is what ends the hourly
 *    reconnect. Active only where the deployment has the OAuth client configured (proxy
 *    lib/taGmailOauth.js); a 503 from the token route means "not here", and the paths below take
 *    over exactly as before.
 *
 * 2. SILENT RE-MINT via Google Identity Services (gmailSilentGis.ts), using the SAME OAuth client
 *    the app signs in with (Firebase Auth's Google provider). Scope grants are per client id, so
 *    once a recruiter has approved the Gmail scopes through the Firebase popup, GIS can mint fresh
 *    access tokens against that standing grant: its window opens and closes itself with no
 *    interaction. Requires the app's origins to be registered as Authorized JavaScript Origins on
 *    that OAuth client in the GCP console (finn-agentspace); where they are not, GIS fails into the
 *    error callback and we fall through to the popup, i.e. exactly the pre-GIS behaviour.
 *
 * 3. FIREBASE POPUP (`reauthenticateWithPopup`) — the first-ever grant where no server flow exists,
 *    and the fallback whenever neither path above can deliver.
 *    reauthenticateWithPopup rather than signInWithPopup when someone is already signed in: it
 *    refuses to complete against a DIFFERENT Google account (auth/user-mismatch), so the token
 *    cannot belong to anyone but the signed-in recruiter. The GIS path has no such client-side
 *    check (only a login hint) — for all paths the proxy re-checks server-side
 *    (verifyMailboxOwner), which is the enforcement point that actually counts.
 *
 * The token is cached in sessionStorage, stamped with its owner and expiry. This is a deliberate
 * revision of the earlier never-persist rule, so reloading the page stops demanding a fresh
 * grant: sessionStorage is scoped to the browser tab and gone when it closes, the entry is
 * ignored once expired or when the signed-in user changed, and the token inside it was already
 * readable by any script on this page while it sat in memory — the marginal exposure is small and
 * the recurring popup it removed was the tab's biggest usability complaint. localStorage keeps
 * only a per-user boolean marker saying "has granted before", so a fresh session knows a silent
 * attempt is worth making; the marker is not a credential.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  GoogleAuthProvider,
  reauthenticateWithPopup,
  signInWithPopup,
  type UserCredential,
} from 'firebase/auth';
import { auth } from '../../../../firebase';
import { logAnalyticsEvent as trackEvent } from '../../../../analytics/nativeAnalytics';
import { EXPIRY_SLACK_MS, GMAIL_SCOPES, grantCoversScopes, type Grant } from './gmailGrant';
import { gisSilentMintEnabled, silentMint } from './gmailSilentGis';
import {
  consumeReturnFlags,
  currentReturnPath,
  isServerGrantUnavailable,
  markServerGrantUnavailable,
  mintFromServer,
  navigateToConsent,
  resetReturnFlagsForTests,
  resetServerGrantMemory,
  startGmailConnect,
} from './gmailServerGrant';

export { GMAIL_SCOPES } from './gmailGrant';
export { GOOGLE_OAUTH_CLIENT_ID } from './gmailSilentGis';
export { setNavigateForTests } from './gmailServerGrant';

/** Renew a server-minted token this long before its (already shortened) expiry. */
const RENEW_LEAD_MS = 2 * 60_000;
/** Never schedule a renewal sooner than this — a token that short-lived is renewed by the 401 path. */
const RENEW_MIN_DELAY_MS = 5_000;

const STORE_KEY = 'ta.gmail.grant';
const consentMarkerKey = (email: string) => `ta.gmail.consented:${email.trim().toLowerCase()}`;

// ─────────────────────────── grant persistence ───────────────────────────

/**
 * Module scope backed by sessionStorage.
 *
 * Module scope (not component state) so the grant survives leaving and re-entering the tab —
 * RequestsTab unmounts on every tab switch. sessionStorage so it also survives a reload, for as
 * long as the token itself is valid.
 */
let sessionGrant: Grant | null = null;

function readStoredGrant(): Grant | null {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Grant>;
    if (typeof parsed?.token !== 'string' || typeof parsed?.owner !== 'string') return null;
    if (typeof parsed?.expiresAt !== 'number') return null;
    // A grant stored before scopes were recorded cannot be shown to cover them: treat as absent.
    if (!Array.isArray(parsed?.scopes)) return null;
    return parsed as Grant;
  } catch {
    return null;
  }
}

function persistGrant(grant: Grant | null) {
  sessionGrant = grant;
  try {
    if (grant) sessionStorage.setItem(STORE_KEY, JSON.stringify(grant));
    else sessionStorage.removeItem(STORE_KEY);
  } catch {
    // Storage full or blocked — the in-memory grant still works for this page's lifetime.
  }
}

/** Remember (in localStorage, per user) that a consent screen was completed once. */
function markConsented(owner: string) {
  try {
    localStorage.setItem(consentMarkerKey(owner), '1');
  } catch {
    /* marker is an optimisation only */
  }
}

export function hasConsentedBefore(userEmail: string): boolean {
  try {
    return localStorage.getItem(consentMarkerKey(userEmail)) === '1';
  } catch {
    return false;
  }
}

/** @internal test seam — forget the page-level grant and the "server unavailable" memory. */
export function resetGmailSession() {
  persistGrant(null);
  resetServerGrantMemory();
  resetReturnFlagsForTests();
}

/**
 * The stored grant, but only if it belongs to this user and has not expired.
 *
 * The owner check matters when someone signs out and back in as somebody else without a reload:
 * the previous recruiter's token must never be attached to the new user's requests. The proxy
 * rejects that anyway (verifyMailboxOwner) — but it fails confusingly, and the client should not
 * be sending another person's credential at all.
 */
function grantFor(userEmail: string): Grant | null {
  const owner = userEmail.trim().toLowerCase();
  if (!sessionGrant) sessionGrant = readStoredGrant();
  if (!sessionGrant || sessionGrant.owner !== owner) return null;
  if (Date.now() >= sessionGrant.expiresAt) return null;
  // A token minted before the app asked for a wider scope is unusable for the new call.
  if (!grantCoversScopes(sessionGrant)) return null;
  return sessionGrant;
}

function tokenFor(userEmail: string): string | null {
  return grantFor(userEmail)?.token ?? null;
}

// ─────────────────────────── firebase popup fallback ───────────────────────────

function gmailProvider(userEmail: string, forceConsent: boolean) {
  const provider = new GoogleAuthProvider();
  for (const scope of GMAIL_SCOPES) provider.addScope(scope);
  provider.setCustomParameters({
    // login_hint pre-selects the signed-in account, so a recruiter with several Google accounts
    // is not steered into authorising the wrong mailbox.
    login_hint: userEmail,
    // Force the scope screen on the first grant; omitted afterwards so a re-grant is one click.
    ...(forceConsent ? { prompt: 'consent' } : {}),
  });
  return provider;
}

/** Human-readable cause, from Firebase's error codes. */
function describe(err: unknown): string {
  const code = (err as { code?: string })?.code || '';
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
    return 'The Google window was closed before access was granted.';
  }
  if (code === 'auth/popup-blocked') {
    return 'The Google window was blocked — allow popups for this site and try again.';
  }
  if (code === 'auth/user-mismatch') {
    return 'That is a different Google account than the one you are signed in with.';
  }
  const message = (err as { message?: string })?.message || '';
  // Surfaces the admin-policy and unapproved-scope cases, which are the ones worth reading.
  if (/admin_policy_enforced|access_denied|disallowed_useragent|invalid_scope/i.test(`${code} ${message}`)) {
    return 'Google refused the Gmail permissions for this app. A Workspace admin may need to allow it.';
  }
  return message || 'Could not get Gmail access.';
}

/**
 * Firebase tokens carry no expiry alongside them; Google web access tokens last an hour, so
 * assume that and let the 401-handling in RequestsTab cover the rare shorter one.
 */
const FIREBASE_TOKEN_LIFETIME_MS = 3600 * 1000;

// ─────────────────────────── the hook ───────────────────────────

export interface GmailAuth {
  token: string | null;
  connected: boolean;
  connecting: boolean;
  error: string | null;
  /** Ask Google for Gmail access and return the token, or null if it did not happen. */
  connect: () => Promise<string | null>;
  clear: () => void;
}

export function useGmailToken(userEmail: string): GmailAuth {
  // Seeded from the persisted grant so a remount (tab switch) or reload does not re-prompt — but
  // only when that grant was made by the user who is signed in now, and has not expired.
  const [token, setTokenState] = useState<string | null>(() => tokenFor(userEmail));
  const [connecting, setConnecting] = useState(false);
  // Read once per mount: the flags Google's redirect (via the proxy callback) left on the URL.
  const [returnFlags] = useState(consumeReturnFlags);
  const [error, setError] = useState<string | null>(returnFlags.error);

  const setGrant = useCallback(
    (grant: Grant | null) => {
      persistGrant(grant);
      setTokenState(grant ? grant.token : null);
    },
    []
  );

  const connect = useCallback(async (): Promise<string | null> => {
    setConnecting(true);
    setError(null);
    // Set when the page is about to leave for Google's consent screen: the button then keeps its
    // busy state until the navigation lands instead of flickering back for a frame.
    let leaving = false;
    try {
      const owner = userEmail.trim().toLowerCase();

      // Server grant first. With one in place this is silent — the hourly reconnect the tab used to
      // ask for is exactly this call happening without anyone noticing. Without one, Google's own
      // consent page (a full-page redirect that brings the browser back here) is what CREATES the
      // grant, so from then on every hour and every reload is silent too.
      if (!isServerGrantUnavailable()) {
        const minted = await mintFromServer(owner);
        if ('grant' in minted) {
          setGrant(minted.grant);
          trackEvent('gmail_connected', { method: 'server' });
          return minted.grant.token;
        }
        if ('error' in minted) {
          // A proxy hiccup is not "no grant": sending the recruiter through the consent page again
          // would mint a needless second grant. Say what happened and let them retry.
          setError(`Could not refresh the Gmail connection: ${minted.error}`);
          trackEvent('gmail_connect_failed', { reason: 'server_mint_error' });
          return null;
        }
        if (!isServerGrantUnavailable()) {
          const start = await startGmailConnect(currentReturnPath());
          if (start.kind === 'redirect') {
            leaving = true;
            trackEvent('gmail_connect_started', { method: 'server', reconnect: 'needsReconnect' in minted });
            navigateToConsent(start.authorizeUrl);
            return null;
          }
          if (start.kind === 'error') {
            setError(start.message);
            trackEvent('gmail_connect_failed', { reason: 'server_start_failed' });
            return null;
          }
          // 'unavailable' — the deployment has no client; the paths below are the whole story here.
          markServerGrantUnavailable();
        }
      }

      // Silent GIS next: with a standing grant this window closes itself, no clicks and no consent
      // screen. Skipped before the first-ever grant, where it could only fail — and skipped
      // entirely until the origins are registered (see gisSilentMintEnabled).
      if (gisSilentMintEnabled() && hasConsentedBefore(owner)) {
        const minted = await silentMint(owner);
        /**
         * Accept the silent token only when it covers what the app now needs. A pre-escalation
         * standing grant mints happily — minus gmail.modify. Accepting it would trap the recruiter
         * in a loop the connect button cannot break: banner → reconnect → same silent token → the
         * next write bounces → banner. Falling through to the popup is what ends it, because the
         * popup shows Google's consent screen for exactly the missing scope.
         */
        if (minted && grantCoversScopes(minted)) {
          setGrant(minted);
          trackEvent('gmail_connected', { method: 'silent' });
          return minted.token;
        }
      }

      const provider = gmailProvider(userEmail, !hasConsentedBefore(owner));
      const result: UserCredential = auth.currentUser
        ? await reauthenticateWithPopup(auth.currentUser, provider)
        : await signInWithPopup(auth, provider);

      const accessToken = GoogleAuthProvider.credentialFromResult(result)?.accessToken;
      if (!accessToken) {
        setError('Google did not return Gmail access. Try again, and approve both permissions.');
        trackEvent('gmail_connect_failed', { reason: 'no_token_returned' });
        return null;
      }
      const firstGrant = !hasConsentedBefore(owner);
      markConsented(owner);
      setGrant({
        token: accessToken,
        owner,
        expiresAt: Date.now() + FIREBASE_TOKEN_LIFETIME_MS - EXPIRY_SLACK_MS,
        scopes: [...GMAIL_SCOPES],
        source: 'popup',
      });
      // After setGrant, always: telemetry must never be able to void a valid authorisation.
      trackEvent('gmail_connected', { method: 'popup', firstGrant });
      return accessToken;
    } catch (err) {
      setError(describe(err));
      return null;
    } finally {
      if (!leaving) setConnecting(false);
    }
  }, [userEmail, setGrant]);

  /**
   * Obtain a token on our own initiative when there is a way to do so without anyone clicking:
   * the proxy's stored grant first (fresh page, new browser session, back from Google's consent
   * page), then the GIS standing grant where that path is enabled. Runs at most once per mount.
   * When nothing silent can deliver, the ordinary Connect button remains the path.
   */
  useEffect(() => {
    if (token || connecting) return;
    let cancelled = false;
    void (async () => {
      const minted = await mintFromServer(userEmail);
      if (cancelled) return;
      if ('grant' in minted) {
        // Never clobber a token a concurrent connect() obtained in the meantime.
        if (!tokenFor(userEmail)) setGrant(minted.grant);
        trackEvent('gmail_connected', { method: 'server_auto', afterConsent: returnFlags.connected });
        return;
      }
      if ('needsReconnect' in minted) return; // the Connect button now leads to Google's consent page
      if (!gisSilentMintEnabled() || !hasConsentedBefore(userEmail)) return;
      const gis = await silentMint(userEmail);
      if (!cancelled && gis && !tokenFor(userEmail)) setGrant(gis);
    })();
    return () => {
      cancelled = true;
    };
    // Mount + user only: a token expiring mid-visit is handled by the renewal timer below and by
    // RequestsTab's reconnect flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  /**
   * Renew a server-minted token shortly before it lapses, so an afternoon on the board never hits
   * the expired banner. Only for tokens the proxy can renew silently; a popup-sourced token has
   * nothing behind it to renew from and keeps the old 401 → reconnect flow.
   */
  useEffect(() => {
    if (!token) return;
    const grant = grantFor(userEmail);
    if (!grant || grant.source !== 'server' || isServerGrantUnavailable()) return;
    const delay = Math.max(RENEW_MIN_DELAY_MS, grant.expiresAt - Date.now() - RENEW_LEAD_MS);
    let cancelled = false;
    const timer = setTimeout(() => {
      void mintFromServer(userEmail).then((minted) => {
        if (cancelled) return;
        if ('grant' in minted) {
          setGrant(minted.grant);
          trackEvent('gmail_connected', { method: 'server_renew' });
        }
        // Anything else: leave the current token in place; when it lapses the 401 path prompts.
      });
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [token, userEmail, setGrant]);

  const clear = useCallback(() => {
    setGrant(null);
    setError(null);
  }, [setGrant]);

  return { token, connected: Boolean(token), connecting, error, connect, clear };
}
